<template>
    <div class="d-flex flex-column">
        <!-- Title & Arrows-->
        <div class="d-flex flex-wrap align-items-center gap-1 mb-2">
            <label class="m-0 flex-grow-1">{{ title }}</label>
            <button v-if="max != 1" v-bind:class="{ disabled: files.length == 0 || files.length == 1 }" class="d-flex justify-content-center align-items-center" @click="$refs.filesPicker.previous()"><CIcon name="cilArrowLeft"></CIcon></button>
            <button v-if="max != 1" :class="{'disabled': files.length >= max}" class="d-flex justify-content-center align-items-center" @click="onChooseFileClicked"><CIcon name="cilPlus"></CIcon></button>
            <button v-if="max != 1" v-bind:class="{ disabled: !files.length || files.length == 1}" class="d-flex justify-content-center align-items-center" @click="$refs.filesPicker.next()"><CIcon name="cilArrowRight"></CIcon></button>
        </div>
        <!-- Single selector (max 1) -->
        <div v-if="max == 1" role="group" class="form-group">
            <input v-if="!value || isLocalFile(value)" class="form-control p-1" ref="fileInput" type="file" :accept="accept" @change="onFileSelect"/>
            <div v-else class="d-flex form-control justify-content-between">
                <a :href="getFilePath(value)" target="_blank">{{$t("View")}}</a>
                <a class="cursor-pointer" @click="removeFile(null)"><u>{{$t("Remove")}}</u></a>
            </div>
        </div> 
        <!-- Slides (max different than one) -->
        <vueper-slides v-else ref="filesPicker" class="mb-1 no-shadow form-control p-0" :touchable="false" fixed-height="35px" :arrows="false" :bullets="false" :infinite="false">
            <!-- Images -->
            <vueper-slide class="position-relative" v-for="(file, index) in files" :key="index">
                <template v-slot:content>
                    <div role="group" class="form-group d-flex align-items-center px-2 h-100">
                        <input v-if="!file" class="multi-input" ref="fileInput" type="file" :accept="accept" @change="onFileSelect" @click="onFileClick(index)"/>
                        <span v-else-if="isLocalFile(file)">{{file.filename}}</span>
                        <a v-else :href="getFilePath(file)" target="_blank">{{$t("View")}}</a>
                        <div class="d-flex justify-content-center align-items-center delete-button h-100 px-2">
                            <button @click="removeFile(index)"><CIcon name="cilMinus"></CIcon></button>
                        </div>
                    </div> 
                </template>     
            </vueper-slide>
            <!-- Placeholder -->
            <vueper-slide v-if="!files.length">
                <template v-slot:content>
                    <span class="d-flex align-items-center px-2 h-100">{{$t('noDocuments')}}</span>
                </template>  
            </vueper-slide>
        </vueper-slides>
        <!-- Invalid feedback -->
        <div class="invalid-feedback">{{invalidFeedback}}</div>
    </div>
</template>

<script>
import { convertToBase64 } from "@/helpers/utils.js";
import { VueperSlides, VueperSlide } from 'vueperslides'
import 'vueperslides/dist/vueperslides.css'
import { API_HELPER } from "@/helpers/api.js";

const MAX_SIZE = 1; // MB

export default {
    components: { VueperSlides, VueperSlide },
    props: {
        value: null, // MAX 1 means object, others is array
        title: String,
        accept: {
            type: String,
            default: ".pdf"
        },
        mime: {
            type: String
        },
        invalidFeedback:
        {
            type: String,
            default: null
        },
        max: {
            type: Number,
            default: 0 // Infinite
        },
    },
    data() {
        return {
            files: this.value ? (Array.isArray(this.value) ? this.value : [this.value]) : []
        }
    },
    methods: {
        removeFile(index)
        {
            if(index == null)
            {
                this.files = null;
            }
            else
            {
                this.files.splice(index, 1);
            }
        },
        onChooseFileClicked()
        {
            this.files.push(null);

            this.$nextTick(() => {
                this.$refs.fileInput[0].click();
            });
        },
        isLocalFile: function(file)
        {
            return file && file.data;
        },
        getFilePath: function(file)
        {
            return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, file.path]);
        },
        // Filter out possible null files. This can happen in case the user adds a new file but doesn't select one on the file dialog.
        // Three event handlers are used:
        // - onchange event on the file input: To detect when a file has been selected.
        // - onfocus event on window: To detect when the Select File Dialog has been closed.
        // - onmousemove event on document.body: To detect when the interaction is not blocked anymore. Only when this event is called, you can be sure that the onchange event of the input element has been called.
        // The first two points are obvious and you find them in most proposed solutions. But the crucial point is number 3. In other solutions I sometimes faced the problem that I selected a file, but this selected file has not been propagated to the onchange event handler before window's got focus.
        onFileClick(index)
        {
            this.$refs.filesPicker.goToSlide(index);

            setTimeout(_ => {
                const onFocus = () => {
                    window.removeEventListener('focus', onFocus);
                    document.body.addEventListener('mousemove', onMouseMove);
                };
                const onMouseMove = () => {
                    document.body.removeEventListener('mousemove', onMouseMove);
                    if (!this.$refs.fileInput[0].files.length) {
                        this.removeFile(index);
                    }
                }
                window.addEventListener('focus', onFocus);
            }, 0);
        },
        onFileSelect(e) 
        {
            try
            {
                var file = e.target.files[0];
                // OnFileSelect is only called when the file changes. So, in order to avoid the user to cancel, the onFileClick exists. Deleting the file on !file was not enough because it only covered scenarios in which the user selects a file and then, for the same file input, cancels the dialog.
                if(file) 
                {
                    var fileName = file.name;
                    var fileType = file.type;
                    var extension = this.mime ?? fileType.split('/').pop();

                    const maxSize = 1024 * 1024 * MAX_SIZE;

                    // Validations
                    if(file.size > maxSize)
                    {
                        throw this.$t('imageErrorMessage1');
                    }
                    
                    convertToBase64(file)
                    .then(base64 =>{
                        let file = {extension: extension, data: base64.split(',')[1], filename: fileName};
                        if(this.files.length) {
                            this.files[this.files.length - 1] = file;
                            this.currentSlide = this.files.length - 1;
                        }else{
                            this.files.push(file);
                        }
                    });
                }
            }
            catch(err)
            {
                alert(err);
            }
        }
    },
    watch:
    {
        value: function(val)
        {
            this.files = val ? (Array.isArray(val) ? val : [val]) : []
        },
        files: function(val) 
        { 
            var file = val && val.length ? val[0] : null;
            var value = this.max == 1 ? file : val;

            this.$emit('input', value);
        }
    }
}
</script>

<style scoped>
.delete-button
{
    position: absolute;
    top: 0;
    right: 0;
}

.is-invalid input
{
    border-color: #e55353;
}

.disabled input
{
    pointer-events: none;
    opacity: 0.7;
}

.is-invalid > .invalid-feedback
{
    display: block;
}

button
{
    border-color: #d8dbe0;
    border-top-color: rgb(216, 219, 224);
    border-right-color: rgb(216, 219, 224);
    border-bottom-color: rgb(216, 219, 224);
    border-left-color: rgb(216, 219, 224);
    border-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}

.multi-input
{
    border: none;
}

</style>